import { useEffect, useState } from 'react';
import { formatNumber } from '../lib/actions';
import { api } from '../lib/api';
import { useNova } from '../lib/store';
import { duration } from './ui';

interface Dashboard {
  timezone: string;
  today: number;
  yesterday: number;
  talk: number;
  avg_talk: number;
  outcomes: { label: string; n: number }[];
  talkByHour: number[];
  days: { day: string; talk: number; calls: number }[];
  latest: {
    id: string;
    direction: 'inbound' | 'outbound';
    status: string;
    customer_number: string | null;
    customer_name: string | null;
    queue_name: string | null;
    started_at: string;
    ended_at: string | null;
    talk_seconds: number | null;
    outcome: string | null;
  }[];
}

/** hh:mm:ss, as the Sync AI portal shows interaction time */
const clock = (s: number) => [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map((n) => String(n).padStart(2, '0')).join(':');
const MARK = 'var(--color-chart-1)';

function Card({ title, children, className = '', action }: { title: string; children: React.ReactNode; className?: string; action?: React.ReactNode }) {
  return (
    <section className={`rounded-lg border border-line bg-hull ${className}`} aria-label={title}>
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <h2 className="text-[14px] font-semibold">{title}</h2>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Tile({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-line bg-hull p-4" aria-label={title}>
      <h2 className="mb-3 text-[13px] text-ink-2">{title}</h2>
      {children}
    </section>
  );
}

/**
 * The agent's home, laid out like the Sync AI Omni portal's dashboard, with
 * what Nova knows: calls handled today, outcomes, talk time, the last 5
 * days and the latest calls. A ringing call takes them to Work.
 */
export function Home() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [error, setError] = useState(false);
  // Refresh when one of my calls finishes
  const trigger = useNova((s) => s.feed.find((e) => e.type === 'call.ended' || e.type === 'interaction.dispositioned')?.eventId);
  const load = () =>
    api<Dashboard>('GET', '/me/dashboard')
      .then((d) => {
        setData(d);
        setError(false);
      })
      .catch(() => setError(true));
  useEffect(() => {
    void load();
  }, [trigger]);

  if (!data) {
    return <p className="p-6 text-sm text-ink-3">{error ? 'Your numbers could not be loaded. Try again in a moment.' : 'Loading…'}</p>;
  }
  const change = data.yesterday ? Math.round(((data.today - data.yesterday) / data.yesterday) * 100) : null;
  const topOutcomes = data.outcomes.slice(0, 4);
  const other = data.outcomes.slice(4).reduce((n, o) => n + o.n, 0);
  const outcomeRows = other ? [...topOutcomes, { label: 'Other', n: other }] : topOutcomes;
  const maxOutcome = Math.max(1, ...outcomeRows.map((o) => o.n));

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile title="Interactions">
          <p className="flex items-baseline gap-2">
            <span className="tabular text-[22px] font-bold">{data.today}</span>
            {change !== null ? (
              <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${change < 0 ? 'bg-break/10 text-break' : 'bg-available/10 text-available'}`}>
                {change > 0 ? '+' : ''}
                {change}% vs yesterday
              </span>
            ) : null}
          </p>
          <p className="mt-1 text-[12px] text-ink-3">Handled today</p>
        </Tile>

        <Tile title="Interaction outcomes">
          {outcomeRows.length === 0 ? (
            <p className="text-[13px] text-ink-3">No calls today yet.</p>
          ) : (
            <ul className="flex flex-col gap-1.5" aria-label="Outcomes today">
              {outcomeRows.map((o) => (
                <li key={o.label} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 text-[12px]" title={`${o.label}: ${o.n}`}>
                  <span className="truncate text-ink-2">{o.label}</span>
                  <span className="tabular font-semibold">{o.n}</span>
                  <span className="col-span-2 h-1.5 rounded-full bg-abyss">
                    <span className="block h-1.5 rounded-full" style={{ width: `${(o.n / maxOutcome) * 100}%`, background: MARK }} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Tile>

        <Tile title="Interactions time">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="tabular text-[22px] font-bold">{clock(data.talk)}</p>
              <p className="mt-1 text-[12px] text-ink-3">Talking today</p>
            </div>
            <Sparkline values={data.talkByHour} />
          </div>
        </Tile>

        <Tile title="Average talk">
          <p className="tabular text-[22px] font-bold">{duration(data.avg_talk)}</p>
          <p className="mt-1 text-[12px] text-ink-3">Per call today</p>
        </Tile>
      </div>

      <Card title="Interactions duration (last 5 days)">
        <DayBars days={data.days} />
      </Card>

      <Card title="Latest contacts">
        {data.latest.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-ink-3">Your calls appear here.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-[13px]">
              <thead className="text-ink-3">
                <tr className="border-b border-line">
                  {['Date', 'Start time', 'End time', 'Contact', 'Direction', 'Queue', 'Outcome'].map((h) => (
                    <th key={h} className="py-2 pr-4 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.latest.map((r) => {
                  const start = new Date(r.started_at);
                  const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                  return (
                    <tr key={r.id} className="border-b border-line/70 last:border-0">
                      <td className="tabular py-2.5 pr-4">{start.toLocaleDateString()}</td>
                      <td className="tabular py-2.5 pr-4">{time(start)}</td>
                      <td className="tabular py-2.5 pr-4">{r.ended_at ? time(new Date(r.ended_at)) : '–'}</td>
                      <td className="py-2.5 pr-4">{r.customer_name ?? formatNumber(r.customer_number ?? '') ?? '–'}</td>
                      <td className="py-2.5 pr-4 text-ink-2">{r.direction === 'outbound' ? '↗ Outbound' : '↙ Inbound'}</td>
                      <td className="py-2.5 pr-4 text-ink-2">{r.queue_name ?? '–'}</td>
                      <td className={`py-2.5 pr-4 ${r.status === 'abandoned' || r.status === 'failed' ? 'text-break' : ''}`}>
                        {r.outcome ?? (r.status === 'abandoned' || r.status === 'failed' ? 'Not connected' : r.status === 'wrap_up' ? 'Needs wrap-up' : '–')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** Talk time by hour of today: one line, no axes, the hour and minutes on hover. */
function Sparkline({ values }: { values: number[] }) {
  const [hover, setHover] = useState<number | null>(null);
  const w = 112;
  const h = 36;
  const max = Math.max(1, ...values);
  const x = (i: number) => (i / (values.length - 1)) * (w - 4) + 2;
  const y = (v: number) => h - 3 - (v / max) * (h - 6);
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  return (
    <div className="relative">
      <svg width={w} height={h} role="img" aria-label="Talk time by hour today" onMouseLeave={() => setHover(null)}>
        <path d={d} fill="none" stroke={MARK} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {hover !== null ? <circle cx={x(hover)} cy={y(values[hover])} r="4" fill={MARK} stroke="var(--color-hull)" strokeWidth="2" /> : null}
        {values.map((_, i) => (
          <rect key={i} x={x(i) - w / values.length / 2} y="0" width={w / values.length} height={h} fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
      </svg>
      {hover !== null ? (
        <span className="tabular pointer-events-none absolute -top-7 right-0 whitespace-nowrap rounded border border-line bg-hull px-1.5 py-0.5 text-[11px] shadow-sm">
          {String(hover).padStart(2, '0')}:00 · {duration(values[hover])}
        </span>
      ) : null}
    </div>
  );
}

/** Talk time per day, the last 5 days: thin bars from a baseline, exact values on hover. */
function DayBars({ days }: { days: Dashboard['days'] }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...days.map((d) => d.talk));
  const label = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString([], { month: '2-digit', day: '2-digit' });
  return (
    <div className="relative" onMouseLeave={() => setHover(null)}>
      <div className="flex h-[150px] items-end justify-around gap-4 border-b border-line" role="img" aria-label="Talk time per day, last 5 days">
        {days.map((d, i) => (
          <div key={d.day} className="flex h-full w-full max-w-[120px] flex-col items-center justify-end" onMouseEnter={() => setHover(i)}>
            <div
              className="w-full max-w-[44px] rounded-t-[4px]"
              style={{ height: `${Math.max(d.talk ? 4 : 2, (d.talk / max) * 100)}%`, background: d.talk ? MARK : 'var(--color-line-strong)', opacity: hover === null || hover === i ? 1 : 0.55 }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex justify-around gap-4">
        {days.map((d) => (
          <span key={d.day} className="tabular w-full max-w-[120px] text-center text-[11px] text-ink-3">
            {label(d.day)}
          </span>
        ))}
      </div>
      {hover !== null ? (
        <span className="tabular pointer-events-none absolute right-0 top-0 rounded border border-line bg-hull px-2 py-1 text-[12px] shadow-sm">
          {label(days[hover].day)}: {duration(days[hover].talk)} talking, {days[hover].calls} call{days[hover].calls === 1 ? '' : 's'}
        </span>
      ) : null}
      {/* The same numbers as text, for screen readers and anyone who prefers a table */}
      <table className="sr-only">
        <caption>Talk time per day</caption>
        <tbody>
          {days.map((d) => (
            <tr key={d.day}>
              <th>{d.day}</th>
              <td>{duration(d.talk)}</td>
              <td>{d.calls} calls</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
